// "Bu iş kapandı" sinyali — job tepsisi (async-widget) ile sonucu kendi
// tarafında saklayan modallar arasındaki tek bağ.
//
// Sorun: ImageAnimatorModal gibi modallar tamamlanan işin sonucunu
// localStorage'a yazar ve her açılışta geri yükleyip "Onayla" çubuğunu
// gösterir. Kayıt yalnız modalın KENDİ Onayla/× butonunda silinir; iş
// tepsiden onaylanınca/reddedilince/kaldırılınca (ya da başka sekmede,
// sayfa yenilenmeden önce) kayıt bayat kalır ve modal zaten sahneye
// uygulanmış sonucu yeniden "onay bekliyor" diye gösterir.
//
// Çözüm: tepsi kapanan her işin id'sini yayınlar (emitJobsSettled); host
// bayat kayıtları temizler (clearSettledImageEditJobs), açık modallar da
// kendi state'lerini sıfırlar (onJobsSettled).

export const JOB_SETTLED_EVENT = "aidea:jobs-settled";

const IMAGE_EDIT_JOB_PREFIX = "image-edit-job-";
const IMAGE_EDIT_META_PREFIX = "image-edit-job-meta-";

// Onaylanmış / reddedilmiş / silinmiş iş. Tamamlanmış ama onay bekleyen iş
// (approved=false, status=completed) kapanmış SAYILMAZ — onun kaydı kalmalı.
export function isSettledJob(job) {
  if (!job) return false;
  return (
    job.approved === true ||
    job.status === "approved" ||
    job.status === "declined" ||
    !!job.deletedAt
  );
}

// Kapanış türü: "approved" (tepsi onayı sahneye zaten uyguladı) ya da
// "declined" (reddedildi / kaldırıldı / silindi — sonuç istenmiyor).
export const SETTLED_APPROVED = "approved";
export const SETTLED_DECLINED = "declined";

export function settledOutcome(job) {
  if (!isSettledJob(job)) return null;
  return job.approved === true || job.status === "approved" ? SETTLED_APPROVED : SETTLED_DECLINED;
}

export function settledJobIds(jobs) {
  return (Array.isArray(jobs) ? jobs : []).filter(isSettledJob).map((j) => j.id);
}

// Tepsi listelerindeki kapanmış işler → emitJobsSettled girdileri.
export function settledEntries(jobs) {
  return (Array.isArray(jobs) ? jobs : [])
    .filter(isSettledJob)
    .map((j) => ({ id: j.id, outcome: settledOutcome(j) }));
}

// Kalıcı kapanış defteri: { [jobId]: { o: outcome, t: ts } }. Kapanış anında
// mount OLMAYAN bileşenler (sohbet kartı, storyboard satırı…) sonradan
// getSettledOutcome ile sorar. Son SETTLED_REGISTRY_MAX iş tutulur.
const SETTLED_REGISTRY_KEY = "aidea:settled-jobs";
export const SETTLED_REGISTRY_MAX = 300;

function readRegistry() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SETTLED_REGISTRY_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

// Deftere yazar ve ETKİN kapanışları döndürür (daha önce onaylanmış iş,
// sonradan "declined" gelse de onaylı kalır).
function recordSettled(outcomes, now = Date.now()) {
  const effective = { ...outcomes };
  try {
    const reg = readRegistry();
    let changed = false;
    Object.keys(outcomes).forEach((id) => {
      const prev = reg[id];
      // Onay kalıcıdır: sonradan gelen "declined" (ör. onaylı işin listeden
      // kaldırılması) onu ezmesin.
      if (prev && prev.o === SETTLED_APPROVED) effective[id] = SETTLED_APPROVED;
      if (prev && (prev.o === outcomes[id] || prev.o === SETTLED_APPROVED)) return;
      reg[id] = { o: outcomes[id], t: now };
      changed = true;
    });
    if (!changed) return effective;
    const ids = Object.keys(reg);
    if (ids.length > SETTLED_REGISTRY_MAX) {
      ids
        .sort((a, b) => (reg[a].t || 0) - (reg[b].t || 0))
        .slice(0, ids.length - SETTLED_REGISTRY_MAX)
        .forEach((id) => delete reg[id]);
    }
    localStorage.setItem(SETTLED_REGISTRY_KEY, JSON.stringify(reg));
  } catch {
    /* storage yok / dolu — sinyal yine gider */
  }
  return effective;
}

// "approved" | "declined" | null (bilinmiyor / hâlâ bekliyor).
export function getSettledOutcome(jobId) {
  if (!jobId) return null;
  return readRegistry()[jobId]?.o || null;
}

// entries: jobId string'leri ya da { id, outcome }. Düz id → outcome
// `defaultOutcome` (verilmezse "declined").
export function emitJobsSettled(entries, defaultOutcome = SETTLED_DECLINED) {
  const outcomes = {};
  (Array.isArray(entries) ? entries : []).forEach((e) => {
    const id = typeof e === "string" ? e : e?.id;
    if (!id) return;
    // Aynı iş iki listede farklı görünürse onay kazanır (sonuç uygulanmıştır).
    const outcome = (typeof e === "object" && e?.outcome) || defaultOutcome;
    if (outcomes[id] !== SETTLED_APPROVED) outcomes[id] = outcome;
  });
  const ids = Object.keys(outcomes);
  if (!ids.length || typeof window === "undefined") return;
  const effective = recordSettled(outcomes);
  try {
    window.dispatchEvent(
      new CustomEvent(JOB_SETTLED_EVENT, { detail: { jobIds: ids, outcomes: effective } })
    );
  } catch (err) {
    console.warn("[jobSettled] dispatch failed", err);
  }
}

// handler(jobIds, outcomes) — outcomes: { [jobId]: "approved" | "declined" }.
export function onJobsSettled(handler) {
  if (typeof window === "undefined") return () => {};
  const listener = (e) => {
    const ids = e?.detail?.jobIds;
    if (Array.isArray(ids) && ids.length) handler(ids, e.detail.outcomes || {});
  };
  window.addEventListener(JOB_SETTLED_EVENT, listener);
  return () => window.removeEventListener(JOB_SETTLED_EVENT, listener);
}

// ImageAnimatorModal'ın kalıcı kayıtları: `image-edit-job-<boxId>` (jobId +
// sonuç) ve `image-edit-job-meta-<jobId>`. Kutu id'si iş tarafında her zaman
// bilinmediği (ör. mp4 → EnrichedPlayer box-swap'ı kutu id'sini değiştirir)
// için kayıtlar jobId ile taranır. Temizlenen boxId'leri döndürür.
export function clearSettledImageEditJobs(jobIds, storage) {
  const store = storage || (typeof localStorage !== "undefined" ? localStorage : null);
  const ids = new Set((Array.isArray(jobIds) ? jobIds : []).filter(Boolean));
  if (!store || !ids.size) return [];

  const clearedBoxIds = [];
  try {
    const keys = [];
    for (let i = 0; i < store.length; i += 1) keys.push(store.key(i));
    keys.forEach((key) => {
      if (
        !key ||
        !key.startsWith(IMAGE_EDIT_JOB_PREFIX) ||
        key.startsWith(IMAGE_EDIT_META_PREFIX)
      ) {
        return;
      }
      let saved = null;
      try {
        saved = JSON.parse(store.getItem(key));
      } catch {
        return;
      }
      if (saved && ids.has(saved.jobId)) {
        store.removeItem(key);
        clearedBoxIds.push(key.slice(IMAGE_EDIT_JOB_PREFIX.length));
      }
    });
    ids.forEach((id) => store.removeItem(`${IMAGE_EDIT_META_PREFIX}${id}`));
  } catch (err) {
    console.warn("[jobSettled] clear failed", err);
  }
  return clearedBoxIds;
}

// `{ [key]: [{ jobId, ... }] }` biçimli bekleyen-sonuç kovalarından kapanmış
// işlerin girdilerini ayıklar. Değişen bir şey yoksa AYNI nesneyi döndürür
// (setState no-op kalsın). Boşalan kova silinir. `changedKeys`: girdisi düşen
// kovalar — çağıran onlara bağlı index'li seçimleri sıfırlayabilir.
export function dropSettledFromBuckets(buckets, jobIds) {
  const ids = new Set((Array.isArray(jobIds) ? jobIds : []).filter(Boolean));
  if (!buckets || !ids.size) return { next: buckets, changedKeys: [] };
  const changedKeys = [];
  const next = {};
  Object.keys(buckets).forEach((key) => {
    const list = Array.isArray(buckets[key]) ? buckets[key] : [];
    const kept = list.filter((e) => !(e && ids.has(e.jobId)));
    if (kept.length !== list.length) changedKeys.push(key);
    if (kept.length) next[key] = kept;
  });
  return changedKeys.length ? { next, changedKeys } : { next: buckets, changedKeys };
}
